function parsePartialJson(chunk: string): Record<string, unknown> | null {
  try {
    const outer = JSON.parse(chunk); // { response: "..." }

    if (typeof outer.response === 'string') {
      // Try full parse of the inner JSON
      try {
        return JSON.parse(outer.response);
      } catch {
        const result: Record<string, unknown> = {};

        // Parse known string fields like title, subtitle, cardType
        const fields = ['title', 'subtitle', 'cardType', 'variant'];
        for (const field of fields) {
          const match = outer.response.match(
            new RegExp(`"${field}"\\s*:\\s*"([^"]*)`),
          );
          if (match) result[field] = match[1];
        }

        // Parse individual options from the array
        const options = parseIndividualOptions(outer.response);
        if (options.length > 0) {
          result.options = options;
        }

        return Object.keys(result).length > 0 ? result : null;
      }
    }
  } catch (e) {
    window.dev.e('parsePartialJson failed:', e);
  }

  return null;
}

function parseIndividualOptions(
  jsonString: string,
): Array<Record<string, unknown>> {
  const options: Array<Record<string, unknown>> = [];

  // Find the start of the options array
  const optionsStart = jsonString.match(/"options"\s*:\s*\[/);
  if (!optionsStart) return options;

  const startIndex = optionsStart.index! + optionsStart[0].length;
  const currentIndex = startIndex;
  let braceCount = 0;
  let inString = false;
  let escapeNext = false;
  let objectStart = -1;

  for (let i = currentIndex; i < jsonString.length; i++) {
    const char = jsonString[i];

    if (escapeNext) {
      escapeNext = false;
      continue;
    }

    if (char === '\\') {
      escapeNext = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (inString) continue;

    if (char === '{') {
      if (braceCount === 0) {
        objectStart = i;
      }
      braceCount++;
    } else if (char === '}') {
      braceCount--;

      if (braceCount === 0 && objectStart !== -1) {
        // We have a complete object
        const objectJson = jsonString.substring(objectStart, i + 1);
        try {
          const parsedOption = JSON.parse(objectJson);
          options.push(parsedOption);
        } catch (e) {
          window.dev.e('Failed to parse individual option:', objectJson);
        }
        objectStart = -1;
      }
    } else if (char === ']') {
      // End of options array
      break;
    }
  }

  // Handle incomplete object at the end (for streaming)
  if (braceCount > 0 && objectStart !== -1) {
    const incompleteObject = jsonString.substring(objectStart);
    const partialOption = parsePartialOption(incompleteObject);
    if (partialOption && Object.keys(partialOption).length > 0) {
      options.push(partialOption);
    }
  }

  return options;
}

function parsePartialOption(
  jsonString: string,
): Record<string, unknown> | null {
  const result: Record<string, unknown> = {};

  // Common option fields - adjust based on your actual option structure
  const fields = ['title', 'subtitle', 'cardType', 'variant'];
  for (const field of fields) {
    // Match string values
    const stringMatch = jsonString.match(
      new RegExp(`"${field}"\\s*:\\s*"([^"]*)"`),
    );
    if (stringMatch) {
      result[field] = stringMatch[1];
      continue;
    }

    // Match boolean values
    const boolMatch = jsonString.match(
      new RegExp(`"${field}"\\s*:\\s*(true|false)`),
    );
    if (boolMatch) {
      result[field] = boolMatch[1] === 'true';
      continue;
    }

    // Match number values
    const numberMatch = jsonString.match(
      new RegExp(`"${field}"\\s*:\\s*(-?\\d+(?:\\.\\d+)?)`),
    );
    if (numberMatch) {
      result[field] = parseFloat(numberMatch[1]);
      continue;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export default parsePartialJson;
